import { Observations } from '@/components/Modals/SetupWorkflow/Forms/Observations';
import { Samples } from '@/components/Modals/SetupWorkflow/Forms/Samples';
import Button from '@/components/UI/Button';
import { _isEmpty, _isNil, _isNotEmpty } from '@/littledash';
import type { Dispatch, FC, SetStateAction } from 'react';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form-latest';
import { RiArrowDropRightFill } from 'react-icons/ri';
import { useDispatch } from 'react-redux';
import { Dosing, General, Measurements } from './Forms';
import { assembleInitialWorkflow, WORKFLOW_TAB_TYPE } from './SetupWorkflow.utils';
import type { Study } from '@/model/Study.model.ts';
import type { WorkflowSetup, WorkflowTabConfig } from '@/components/Workflow/Show/Workflow.model.ts';
import Consumption from '@/components/Modals/SetupWorkflow/Forms/Consumption.tsx';
import { consumptionMetricIds } from '@/components/Workflow/Show/Views/Consumption/Consumption.utils';
import type { ID } from '@/model/Common.model';
import type { WorkflowTemplateDefaults } from '@/components/Modals/SetupWorkflow/SetupWorkflow.model';

export type SetupWorkflowFormValues = { workflowSetupForm: WorkflowSetup };

interface WorkflowListItem {
  id: number;
  name: string;
  type: string;
  Component: FC<WorkflowListItemProps>;
  workflow?: WorkflowTabConfig;
  setWorkflowState?: Dispatch<SetStateAction<Array<WorkflowTabConfig>>>;
  index?: number;
  workflowState?: Array<WorkflowTabConfig>;
  studyId?: ID;
  study: Study;
}

type WorkflowListItemProps = Omit<WorkflowListItem, 'Component'>;

interface SetupWorkflowProps {
  study: Study;
  workflowSetup: WorkflowSetup;
  updateWorkflowSetup: (workflow: WorkflowSetup) => void;
  templateDefaults?: WorkflowTemplateDefaults;
  handleCallback: () => void;
}

const SetupWorkflow: FC<SetupWorkflowProps> = ({
  study,
  workflowSetup,
  updateWorkflowSetup,
  templateDefaults,
  handleCallback,
}) => {
  const calculations = study?.settings?.calculations ?? [];
  const consumptionEnabled = calculations.some((calculation) => consumptionMetricIds.has(calculation.id));
  const [workflowState, setWorkflowState] = useState(() =>
    _isEmpty(workflowSetup.workflowTabConfig)
      ? assembleInitialWorkflow(calculations, [], templateDefaults, consumptionEnabled)
      : [...workflowSetup.workflowTabConfig]
  );

  const formMethods = useForm<SetupWorkflowFormValues>({
    defaultValues: {
      workflowSetupForm: {
        idToSearch: workflowSetup.idToSearch,
        idToDisplay: workflowSetup.idToDisplay,
        measured_at: workflowSetup.measured_at,
        showGroups: workflowSetup.showGroups,
        dosingData: workflowSetup.dosingData,
        playSound: workflowSetup.playSound,
        workflowTabConfig: assembleInitialWorkflow(
          calculations,
          workflowSetup.workflowTabConfig,
          {
            measurements: templateDefaults?.measurements,
            samples: templateDefaults?.samples,
            observations: templateDefaults?.observations,
            dosing: templateDefaults?.dosing,
          },
          consumptionEnabled
        ),
        startTab: workflowSetup.startTab,
        foodIntakeEnabled: workflowSetup.foodIntakeEnabled,
        fluidIntakeEnabled: workflowSetup.fluidIntakeEnabled,
        intakeTimeEnabled: workflowSetup.intakeTimeEnabled,
      },
    },
    shouldUnregister: false,
  });

  const { handleSubmit } = formMethods;
  const listItems = getListItems(workflowState, setWorkflowState, study);
  const [selectedFlow, setSelectedFlow] = useState(1);
  const storeDispatch = useDispatch();

  const submit = ({ workflowSetupForm }: SetupWorkflowFormValues) => {
    updateWorkflowSetup(workflowSetupForm);
    handleCallback();
  };

  const selectedItem = listItems.find((item) => item.id === selectedFlow);

  if (_isNil(selectedItem)) {
    return null;
  }

  const { Component: Panel, ...panelProps } = selectedItem;

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(submit)} data-testid="workflow-modal-container">
        <div className="center mv3 bg-white br2 shadow-4" style={{ maxWidth: '900px' }}>
          <div className="pa3 bb b--moon-gray">
            <h3 className="basier-med normal f4 lh-title near-black">Workflow setup</h3>
          </div>
          <div className="w-100 flex flex-wrap ui__workflow__expand">
            <LeftSidePanel listItems={listItems} selectedFlow={selectedFlow} setSelectedFlow={setSelectedFlow} />
            <div className="bg-white flex flex-column justify-apart h-100 w-75">
              <Panel {...panelProps} />
            </div>
          </div>
          <div className="pa3 bt b--moon-gray">
            <Button testId="workflow-setup-submit" submit>
              Continue
            </Button>
            <Button
              plain
              className="ml3"
              onClick={() =>
                storeDispatch({
                  type: 'CLOSE_MODAL',
                })
              }
            >
              Cancel
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

interface LeftSidePanelProps {
  listItems: Array<WorkflowListItem>;
  selectedFlow: number;
  setSelectedFlow: Dispatch<SetStateAction<number>>;
}

const LeftSidePanel: FC<LeftSidePanelProps> = ({ listItems, selectedFlow, setSelectedFlow }) => {
  const inActiveMenuItemClasses = 'hover-blue hover-bg-moon-gray near-black';
  const activeMenuItemClasses = 'bg-near-white blue';

  return (
    <div className="br b--moon-gray w-25 h-100">
      <div className="mt3">
        {listItems.map((item, index) => (
          <div key={index}>
            <a
              onClick={() => setSelectedFlow(item.id)}
              className={`pointer flex justify-between items-center pl3 pr2 pv2 mh2 br1
                  ${item.id === selectedFlow ? activeMenuItemClasses : inActiveMenuItemClasses}
                `}
              data-testid={`${item.type}-setup-nav-link`}
            >
              <span className="f6 lh-title">{item.name}</span>
              <RiArrowDropRightFill size={22} />
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};

const RenderPanel: FC<WorkflowListItemProps> = ({ type, study }) => {
  switch (type) {
    case WORKFLOW_TAB_TYPE.MEASUREMENTS:
      return <Measurements />;
    case WORKFLOW_TAB_TYPE.SAMPLES:
      return <Samples />;
    case WORKFLOW_TAB_TYPE.OBSERVATIONS:
      return <Observations />;
    case WORKFLOW_TAB_TYPE.DOSING:
      return <Dosing studyApiId={study.api_id} />;
    case WORKFLOW_TAB_TYPE.CONSUMPTION:
      return <Consumption />;
    default: {
      const _exhaustive = type as unknown as never;
      return _exhaustive;
    }
  }
};

const getListItems = (
  workflowState: Array<WorkflowTabConfig>,
  setWorkflowState: Dispatch<SetStateAction<Array<WorkflowTabConfig>>>,
  study: Study
): Array<WorkflowListItem> => {
  const results: Array<WorkflowListItem> = [
    {
      id: 1,
      name: 'General',
      type: 'general',
      study,
      Component: General,
    },
  ];

  if (_isNotEmpty(workflowState)) {
    return [
      ...results,
      ...workflowState.map<WorkflowListItem>((w, i) => ({
        id: i + 2,
        name: w.displayName,
        type: w.name,
        workflow: w,
        setWorkflowState,
        index: i,
        workflowState,
        studyId: study.id,
        study,
        Component: RenderPanel,
      })),
    ];
  }

  return results;
};

export default SetupWorkflow;
